import { useState } from "react";
import {
  ChevronDownIcon,
  FileTextIcon,
  PaperclipIcon,
  PlusIcon,
  XIcon,
} from "lucide-react";

import { JobAttachmentViewerDialog } from "./JobAttachmentViewerDialog";
import { CreateJobFileDialog } from "@/components/companies/jobs/CreateJobFileDialog";
import { Column, ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { JobFile, JobIntake } from "@/types";

export const JobIntakeAttachments = ({
  jobIntake,
}: {
  jobIntake: JobIntake;
}) => {
  /* Database CRUD Operations */
  // Update
  const { mutate: updateJobIntake } = useUpdateSetJobIntake();

  /* React Hooks */
  // useState
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<{
    id: number;
    mimetype: string;
  } | null>(null);
  const [deleteAttachmentId, setDeleteAttachmentId] =
    useState<number | null>(null);

  /* Helpers */
  const attachments = jobIntake.attachments ?? [];

  /* Handlers */
  const handleDeleteAttachment = (id: number) => {
    updateJobIntake({
      updateJobIntakeRequest: {
        attachments: attachments.filter((a) => a.id !== id),
      },
    });
    setDeleteAttachmentId(null);
  };

  const handleNewAttachment = (newJobFile?: JobFile) => {
    if (!newJobFile) return;
    updateJobIntake({
      updateJobIntakeRequest: {
        attachments: [
          ...attachments,
          {
            id: newJobFile.id,
            name: newJobFile.name,
            url: newJobFile.url,
            mimetype: newJobFile.mimeType,
          },
        ],
      },
    });
    setDialogOpen(false);
  };

  /* Render */
  const render = () => (
    <>
      <Collapsible className="group/collapsible mt-2 border-t border-slate-200">
        <CollapsibleTrigger asChild>
          <Button
            className="w-full justify-between px-2 hover:bg-slate-50"
            variant="ghost"
          >
            <Row className="items-center gap-2">
              <PaperclipIcon className="h-3.5 w-3.5 text-slate-500" />
              <Label className="text-[15px] font-medium text-slate-500">
                Attachments ({attachments.length})
              </Label>
            </Row>
            <ChevronDownIcon className="h-3.5 w-3.5 text-slate-400 transition-transform group-data-[state=open]/collapsible:rotate-180" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-1 px-2 pb-2">
            {attachments.map((attachment) => (
              <Row key={attachment.id} className="group/attachment relative">
                <Button
                  className="w-full justify-start px-2 hover:bg-slate-50 group-hover/attachment:pr-8"
                  onClick={() => {
                    setSelectedAttachment({
                      id: attachment.id,
                      mimetype: attachment.mimetype,
                    });
                  }}
                  variant="ghost"
                >
                  <FileTextIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <Label
                    className="truncate text-[12px] text-slate-700"
                    title={attachment.name}
                  >
                    {attachment.name}
                  </Label>
                </Button>
                <Button
                  className="absolute right-1 top-1/2 hidden h-5 w-5 -translate-y-1/2 text-slate-400 hover:bg-transparent hover:text-slate-900 group-hover/attachment:flex"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDeleteAttachmentId(attachment.id);
                  }}
                  size="icon"
                  variant="ghost"
                >
                  <XIcon className="h-3 w-3" />
                </Button>
              </Row>
            ))}
            <Button
              className="hover:border-primary hover:text-primary hover:bg-primary/5 mt-1 w-full border-dashed border-slate-300 text-[12px] text-slate-500"
              onClick={() => {
                setDialogOpen(true);
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              <PlusIcon className="h-3 w-3" />
              Add Attachment
            </Button>
          </Column>
        </CollapsibleContent>
      </Collapsible>

      <CreateJobFileDialog
        onOpenChange={setDialogOpen}
        onSuccess={handleNewAttachment}
        open={dialogOpen}
      />

      <ConfirmationDialog
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        confirmationButtonVariant="destructive"
        description="This will remove the attachment from the job intake."
        onConfirm={() => {
          if (deleteAttachmentId != null) {
            handleDeleteAttachment(deleteAttachmentId);
          }
        }}
        onOpenChange={(open) => {
          if (!open) setDeleteAttachmentId(null);
        }}
        open={deleteAttachmentId != null}
        title="Delete attachment?"
      />

      <JobAttachmentViewerDialog
        jobFileId={selectedAttachment?.id}
        mimeType={selectedAttachment?.mimetype}
        onOpenChange={(open) => {
          if (!open) setSelectedAttachment(null);
        }}
        open={!!selectedAttachment}
      />
    </>
  );

  return render();
};
